import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useInfiniteQuery, useQuery, keepPreviousData } from '@tanstack/react-query';
import { Fragment, useCallback, useRef, useState } from 'react';
import { FiChevronDown, FiChevronRight, FiX } from 'react-icons/fi';
import { MessageBubble } from '@/components/MessageBubble';
import { apiJson } from '@/lib/api';
import { formatPacificDateTime } from '@/lib/adminPacificTime';
import type {
  AdminChatListItem,
  AdminChatType,
  AdminChatsListResponse,
  MessageListResponse,
} from '@/lib/types';

const PAGE_SIZE = 50;

const ADMIN_USER_LINK_CLASS =
  'inline-flex px-2 py-1 rounded-md border border-nova-input text-xs font-semibold text-nova-foreground hover:bg-nova-accent';

function chatRowKey(chat: AdminChatListItem): string {
  return `${chat.chat_type}:${chat.thread_id}`;
}

function activityAt(chat: AdminChatListItem): string {
  return chat.last_message_at ?? chat.created_at;
}

function typeLabel(chatType: AdminChatType): string {
  switch (chatType) {
    case 'main':
      return 'Main';
    case 'custom':
      return 'Custom';
    case 'expense-dispute':
      return 'Expense dispute';
    case 'payment-dispute':
      return 'Payment dispute';
  }
}

function typeBadgeClass(chatType: AdminChatType): string {
  switch (chatType) {
    case 'main':
      return 'bg-sky-100 text-sky-800 border-nova-border';
    case 'custom':
      return 'bg-violet-100 text-violet-800 border-violet-200';
    case 'expense-dispute':
      return 'bg-amber-100 text-amber-900 border-amber-200';
    case 'payment-dispute':
      return 'bg-orange-100 text-orange-900 border-orange-200';
  }
}

function AdminChatMessagesPanel({
  chat,
  expanded,
  onImagePress,
}: {
  chat: AdminChatListItem;
  expanded: boolean;
  onImagePress: (url: string) => void;
}) {
  const { getToken } = useAuth();
  const primaryUserId = chat.member_user_ids[0];

  const getMessagesEndpoint = useCallback(
    (beforeTimestamp?: string) => {
      const params = new URLSearchParams({
        chat_type: chat.chat_type,
        thread_id: String(chat.thread_id),
        limit: '50',
      });
      if (beforeTimestamp) params.set('before_timestamp', beforeTimestamp);
      return `/v1/admin/chats/messages?${params.toString()}`;
    },
    [chat.chat_type, chat.thread_id],
  );

  const messagesQuery = useInfiniteQuery({
    queryKey: ['admin', 'chat-messages', chat.chat_type, chat.thread_id],
    queryFn: async ({ pageParam }) =>
      apiJson<MessageListResponse>(getToken, getMessagesEndpoint(pageParam ?? undefined)),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      if (!lastPage.has_more || lastPage.messages.length === 0) return null;
      const oldest = lastPage.messages[lastPage.messages.length - 1];
      return oldest.timestamp;
    },
    enabled: expanded,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const pages = messagesQuery.data?.pages ?? [];
  const messages = pages.flatMap((p) => p.messages);
  const hasMore = pages.length > 0 && pages[pages.length - 1]?.has_more;
  const totalCount = pages[0]?.total_count;

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (container.scrollTop < 100 && hasMore && !messagesQuery.isFetchingNextPage) {
      messagesQuery.fetchNextPage();
    }
  }, [hasMore, messagesQuery]);

  if (!expanded) return null;

  return (
    <div className='px-3 py-3 bg-nova-muted border-t border-nova-border'>
      <div className='flex flex-wrap items-center justify-between gap-2 mb-2'>
        <p className='text-xs text-nova-muted-foreground'>
          Thread #{chat.thread_id}
          {chat.subtitle ? (
            <>
              <span className='text-nova-muted-foreground'> · </span>
              {chat.subtitle}
            </>
          ) : null}
          {chat.is_archived ? (
            <span className='ml-2 text-[10px] font-semibold uppercase text-nova-muted-foreground'>Archived</span>
          ) : null}
          {chat.is_resolved ? (
            <span className='ml-2 text-[10px] font-semibold uppercase text-emerald-700'>Resolved</span>
          ) : null}
        </p>
        <div className='flex flex-wrap gap-2'>
          {primaryUserId != null ? (
            <Link
              to='/admin/users/$userId/chat'
              params={{ userId: String(primaryUserId) }}
              search={{ hid: String(chat.household_id) }}
              className={ADMIN_USER_LINK_CLASS}
            >
              User chat tab
            </Link>
          ) : null}
          {primaryUserId != null ? (
            <Link
              to='/admin/users/$userId'
              params={{ userId: String(primaryUserId) }}
              search={{ hid: undefined }}
              className={ADMIN_USER_LINK_CLASS}
            >
              User profile
            </Link>
          ) : null}
        </div>
      </div>
      {messagesQuery.isLoading ? (
        <p className='text-xs text-nova-muted-foreground mb-2'>Loading messages…</p>
      ) : totalCount != null ? (
        <p className='text-xs text-nova-muted-foreground tabular-nums mb-2'>
          {totalCount.toLocaleString()} message{totalCount === 1 ? '' : 's'}
        </p>
      ) : chat.message_count > 0 ? (
        <p className='text-xs text-nova-muted-foreground tabular-nums mb-2'>
          {chat.message_count.toLocaleString()} message{chat.message_count === 1 ? '' : 's'}
        </p>
      ) : (
        <p className='text-xs text-nova-muted-foreground mb-2'>No messages yet</p>
      )}

      <div
        ref={containerRef}
        onScroll={handleScroll}
        className='flex flex-col-reverse overflow-y-auto max-h-[min(420px,50vh)] rounded-lg border border-nova-border bg-nova-card px-2 py-2'
      >
        {messagesQuery.isLoading ? (
          <div className='flex justify-center py-8'>
            <div className='animate-spin h-8 w-8 border-2 border-nova-primary border-t-transparent rounded-full' />
          </div>
        ) : messages.length === 0 ? (
          <p className='text-center text-sm text-nova-muted-foreground py-4'>No messages in this thread.</p>
        ) : (
          messages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              currentUserId={-1}
              onImagePress={onImagePress}
            />
          ))
        )}
      </div>
    </div>
  );
}

function AdminChatsPage() {
  const { getToken } = useAuth();
  const [page, setPage] = useState(0);
  const [chatTypeFilter, setChatTypeFilter] = useState<AdminChatType | ''>('');
  const [householdFilter, setHouseholdFilter] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [imageViewerUrl, setImageViewerUrl] = useState<string | null>(null);

  const householdIdParam = householdFilter.trim();
  const householdId =
    householdIdParam && /^\d+$/.test(householdIdParam) ? Number(householdIdParam) : null;

  const chatsQuery = useQuery({
    queryKey: ['admin', 'chats', page, chatTypeFilter, householdIdParam],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(page * PAGE_SIZE));
      if (chatTypeFilter) params.set('chat_type', chatTypeFilter);
      if (householdId != null) params.set('household_id', String(householdId));
      return apiJson<AdminChatsListResponse>(getToken, `/v1/admin/chats?${params.toString()}`);
    },
    placeholderData: keepPreviousData,
  });

  const toggle = useCallback((key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const data = chatsQuery.data;
  const chats = data?.chats ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className='space-y-4'>
      <div>
        <h1 className='text-xl font-semibold text-nova-foreground'>Chats</h1>
        <p className='text-sm text-nova-muted-foreground mt-1'>
          All main, custom, and dispute threads across households. Sorted by most recent message.
          Expand a row to load messages.
        </p>
      </div>

      <div className='bg-nova-card rounded-lg border border-nova-border p-4 flex flex-wrap gap-4 items-end'>
        <div>
          <label className='block text-xs font-semibold text-nova-muted-foreground mb-1'>Type</label>
          <select
            value={chatTypeFilter}
            onChange={(e) => {
              setChatTypeFilter(e.target.value as AdminChatType | '');
              setPage(0);
            }}
            className='px-2 py-1 border border-nova-input rounded text-sm'
          >
            <option value=''>All types</option>
            <option value='main'>Main</option>
            <option value='custom'>Custom</option>
            <option value='expense-dispute'>Expense dispute</option>
            <option value='payment-dispute'>Payment dispute</option>
          </select>
        </div>
        <div>
          <label className='block text-xs font-semibold text-nova-muted-foreground mb-1'>Household ID</label>
          <input
            value={householdFilter}
            onChange={(e) => {
              setHouseholdFilter(e.target.value);
              setPage(0);
            }}
            className='px-2 py-1 border border-nova-input rounded text-sm w-28'
            placeholder='e.g. 12'
            inputMode='numeric'
          />
        </div>
      </div>

      {chatsQuery.isLoading && !data ? (
        <div className='flex justify-center py-16'>
          <div className='animate-spin h-8 w-8 border-2 border-nova-primary border-t-transparent rounded-full' />
        </div>
      ) : chatsQuery.isError ? (
        <p className='text-sm text-red-600'>Could not load chats.</p>
      ) : chats.length === 0 ? (
        <p className='text-sm text-nova-muted-foreground bg-nova-card rounded-lg border border-nova-border p-4'>
          No chats match these filters.
        </p>
      ) : (
        <div className='bg-nova-card rounded-lg border border-nova-border overflow-hidden'>
          <table className='w-full text-sm'>
            <thead className='bg-nova-muted text-left text-xs uppercase text-nova-muted-foreground'>
              <tr>
                <th className='px-3 py-2 w-8' aria-label='Expand' />
                <th className='px-3 py-2'>Type</th>
                <th className='px-3 py-2'>Title</th>
                <th className='px-3 py-2'>Household</th>
                <th className='px-3 py-2'>Last activity</th>
                <th className='px-3 py-2'>Preview</th>
                <th className='px-3 py-2 text-right'>Msgs</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-nova-border'>
              {chats.map((chat) => {
                const key = chatRowKey(chat);
                const isOpen = expanded.has(key);
                const preview = chat.last_message_preview;
                const sender = chat.last_message_sender_first_name;
                const previewLine =
                  preview != null
                    ? sender
                      ? `${sender}: ${preview}`
                      : preview
                    : '—';
                const primaryUserId = chat.member_user_ids[0];

                return (
                  <Fragment key={key}>
                    <tr
                      className='hover:bg-nova-accent cursor-pointer'
                      onClick={() => toggle(key)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggle(key);
                        }
                      }}
                      tabIndex={0}
                    >
                      <td className='px-3 py-2 text-nova-muted-foreground'>
                        {isOpen ? (
                          <FiChevronDown className='h-4 w-4' aria-hidden />
                        ) : (
                          <FiChevronRight className='h-4 w-4' aria-hidden />
                        )}
                      </td>
                      <td className='px-3 py-2'>
                        <span
                          className={`inline-block text-[10px] font-semibold uppercase px-2 py-0.5 rounded border ${typeBadgeClass(chat.chat_type)}`}
                        >
                          {typeLabel(chat.chat_type)}
                        </span>
                      </td>
                      <td className='px-3 py-2 font-medium text-nova-foreground max-w-[200px] truncate'>
                        {chat.title}
                        {chat.is_archived ? (
                          <span className='ml-1 text-[10px] text-nova-muted-foreground'>(archived)</span>
                        ) : null}
                      </td>
                      <td className='px-3 py-2 text-xs tabular-nums'>
                        #{chat.household_id}
                        {primaryUserId != null ? (
                          <Link
                            to='/admin/users/$userId'
                            params={{ userId: String(primaryUserId) }}
                            search={{ hid: String(chat.household_id) }}
                            className={`block mt-0.5 ${ADMIN_USER_LINK_CLASS}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            User #{primaryUserId}
                          </Link>
                        ) : null}
                      </td>
                      <td className='px-3 py-2 whitespace-nowrap text-xs text-nova-muted-foreground tabular-nums'>
                        {formatPacificDateTime(activityAt(chat))}
                      </td>
                      <td className='px-3 py-2 text-xs text-nova-muted-foreground max-w-xs truncate' title={previewLine}>
                        {previewLine}
                      </td>
                      <td className='px-3 py-2 text-right text-xs tabular-nums text-nova-muted-foreground'>
                        {chat.message_count.toLocaleString()}
                      </td>
                    </tr>
                    {isOpen ? (
                      <tr>
                        <td colSpan={7} className='p-0'>
                          <AdminChatMessagesPanel
                            chat={chat}
                            expanded={isOpen}
                            onImagePress={setImageViewerUrl}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > 0 ? (
        <div className='flex items-center justify-between text-sm text-nova-muted-foreground pt-2'>
          <span className='tabular-nums'>
            Showing {page * PAGE_SIZE + 1}–
            {Math.min(page * PAGE_SIZE + chats.length, total)} of {total.toLocaleString()}
          </span>
          <div className='flex gap-2'>
            <button
              type='button'
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || chatsQuery.isFetching}
              className='px-3 py-1 border border-nova-input rounded-md disabled:opacity-50'
            >
              Prev
            </button>
            <span>
              Page {page + 1} / {totalPages}
            </span>
            <button
              type='button'
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages - 1 || chatsQuery.isFetching}
              className='px-3 py-1 border border-nova-input rounded-md disabled:opacity-50'
            >
              Next
            </button>
          </div>
        </div>
      ) : null}

      {imageViewerUrl ? (
        <button
          type='button'
          className='fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4'
          onClick={() => setImageViewerUrl(null)}
          aria-label='Close image'
        >
          <FiX className='absolute top-4 right-4 w-8 h-8 text-white' />
          <img src={imageViewerUrl} alt='' className='max-w-full max-h-full object-contain' />
        </button>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/admin/chats')({
  component: AdminChatsPage,
});
